import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import { FiArrowDownRight, FiArrowRight, FiMonitor } from "react-icons/fi";
import { Magnetic } from "@/components/interactive/Magnetic";
import { useTheme } from "@/components/theme/useTheme";
import { useAccentOption } from "@/components/theme/accent";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useFinePointerMotion } from "@/hooks/useFinePointerMotion";
import { supportsWebGL } from "@/utils/webgl";
import { getScrollBehavior } from "@/utils/scroll";
import "../../styles/hero.css";

const HeroScene = lazy(() => import("@/components/hero/HeroScene"));

const NAME = "Nicholas";
const MIN_WEIGHT = 380;
const MAX_WEIGHT = 800;
const PROXIMITY_RADIUS = 260;

const SCENE_COLORS = {
    dark: { base: "#EDEDEE", additive: true },
    light: { base: "#111112", additive: false },
} as const;

/** Letters thicken as the pointer approaches them, using Geist's variable weight axis. */
function useProximityWeight(enabled: boolean) {
    const containerRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!enabled || !container) return;
        const letters = Array.from(container.querySelectorAll<HTMLElement>("[data-letter]"));
        let frame = 0;

        const onMove = (event: PointerEvent) => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                for (const letter of letters) {
                    const rect = letter.getBoundingClientRect();
                    const dx = event.clientX - (rect.left + rect.width / 2);
                    const dy = event.clientY - (rect.top + rect.height / 2);
                    const closeness = Math.max(0, 1 - Math.hypot(dx, dy) / PROXIMITY_RADIUS);
                    letter.style.fontVariationSettings = `"wght" ${Math.round(MIN_WEIGHT + (MAX_WEIGHT - MIN_WEIGHT) * closeness)}`;
                }
            });
        };

        window.addEventListener("pointermove", onMove, { passive: true });
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("pointermove", onMove);
        };
    }, [enabled]);

    return containerRef;
}

function scrollToSection(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: getScrollBehavior(), block: "start" });
}

export type HeroProps = {
    /** Reopens the tablet / desktop device view. */
    onOpenDevice?: () => void;
};

export default function Hero({ onOpenDevice }: HeroProps) {
    const { t } = useTranslation();
    const { actualTheme } = useTheme();
    const reducedMotion = usePrefersReducedMotion();
    const pointerMotion = useFinePointerMotion();
    const [webgl] = useState(supportsWebGL);
    const { ref: inViewRef, inView } = useInView({ threshold: 0 });
    const nameRef = useProximityWeight(pointerMotion);
    const accent = useAccentOption();
    const colors = { ...SCENE_COLORS[actualTheme], accent: accent[actualTheme] };

    const letterTransition = (index: number) => ({
        duration: reducedMotion ? 0 : 0.9,
        ease: [0.16, 1, 0.3, 1] as const,
        delay: reducedMotion ? 0 : 0.15 + index * 0.05,
    });

    return (
        <section ref={inViewRef} className="hero" aria-label={t("hero.ariaLabel")}>
            <div className="hero-canvas" aria-hidden="true">
                <div className="hero-fallback" />
                {webgl && (
                    <Suspense fallback={null}>
                        <HeroScene
                            active={inView}
                            reducedMotion={reducedMotion}
                            baseColor={colors.base}
                            accentColor={colors.accent}
                            additive={colors.additive}
                        />
                    </Suspense>
                )}
            </div>

            <div className="hero-content">
                <motion.p
                    className="hero-eyebrow"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: reducedMotion ? 0 : 0.6 }}
                >
                    <span className="hero-status-dot" />
                    {t("hero.eyebrow")}
                </motion.p>

                <h1 className="hero-name">
                    <span className="sr-only">Nicholas Y</span>
                    <span ref={nameRef} className="hero-name-row" aria-hidden="true">
                        {NAME.split("").map((char, index) => (
                            <span key={index} className="hero-letter-mask">
                                <motion.span
                                    data-letter
                                    className="hero-letter"
                                    initial={{ y: "110%" }}
                                    animate={{ y: "0%" }}
                                    transition={letterTransition(index)}
                                >
                                    {char}
                                </motion.span>
                            </span>
                        ))}
                        <span className="hero-letter-mask">
                            <motion.span
                                className="hero-letter hero-letter-outline"
                                initial={{ y: "110%" }}
                                animate={{ y: "0%" }}
                                transition={letterTransition(NAME.length + 1)}
                            >
                                Y
                            </motion.span>
                        </span>
                    </span>
                </h1>

                <motion.p
                    className="hero-lede"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: reducedMotion ? 0 : 0.8, delay: reducedMotion ? 0 : 0.7 }}
                >
                    {t("hero.lede")}
                </motion.p>

                <motion.div
                    className="hero-actions"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: reducedMotion ? 0 : 0.8, delay: reducedMotion ? 0 : 0.85 }}
                >
                    <Magnetic>
                        <button type="button" className="hero-btn hero-btn-primary" onClick={() => scrollToSection("portfolio")}>
                            {t("hero.ctaWork")}
                            <FiArrowDownRight className="hero-btn-icon" aria-hidden="true" />
                        </button>
                    </Magnetic>
                    <Magnetic>
                        <button type="button" className="hero-btn hero-btn-ghost" onClick={() => scrollToSection("contact")}>
                            {t("hero.ctaContact")}
                            <FiArrowRight className="hero-btn-icon" aria-hidden="true" />
                        </button>
                    </Magnetic>
                </motion.div>
            </div>

            <div className="hero-meta">
                <span aria-hidden="true">{t("hero.focus")}</span>
                {onOpenDevice && (
                    <button type="button" className="hero-meta-link" onClick={onOpenDevice}>
                        <FiMonitor size={14} aria-hidden="true" />
                        {t("deviceOs.gate.openDesktop")}
                    </button>
                )}
                <span className="hero-scroll-cue" aria-hidden="true">
                    {t("hero.scroll")}
                    <span className="hero-scroll-line" />
                </span>
            </div>
        </section>
    );
}
